import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Plus, Trash2, Loader2, ArrowLeft, Bed, Compass, Bus, UserCheck, Save,
  CalendarDays, IndianRupee, Sparkles, ClipboardPaste, Upload, FileText, X as XIcon, MapPin,
  Heart, Leaf, Package, AlertTriangle
} from 'lucide-react';
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import { formatDate } from '@/lib/dateUtils';
import LocationAutocomplete from '@/components/LocationAutocomplete';
import HotelMapUrlField from '@/components/HotelMapUrlField';
import LocationPinBtn from '@/components/LocationPinBtn';
import { inheritCityPin } from '@/lib/inheritCityPin';
import { usePollenCapabilities } from '@/hooks/usePollenCapabilities';

const emptyHotel = { hotel_name: '', location: '', maps_url: '', check_in: '', check_out: '', nights: 0, meal_plan: '', room_type: '', special_notes: '', contact_name: '', contact_number: '', booking_status: 'Pending', total_amount: 0, advance_paid: 0, balance: 0, payments: [] };
const emptyActivity = { activity_name: '', date: '', time_slot: '', location: '', maps_url: '', contact_details: '', status: 'Pending', total_amount: 0, advance: 0, balance: 0, payments: [] };
const emptyTransport = { vehicle_type: '', route: '', operator_name: '', operator_contact: '', driver_name: '', driver_contact: '', start_date: '', end_date: '', status: 'Pending', total_amount: 0, advance: 0, balance: 0, payments: [] };
const emptyGuide = { guide_name: '', phone: '', email: '', location: '', start_date: '', end_date: '', status: 'Pending', total_amount: 0, advance: 0, balance: 0, payments: [] };
const emptyAgent = { agency_name: '', contact_name: '', contact_number: '', total_package_cost: 0, advance_paid: 0, balance: 0, payments: [] };

const PollenDotsAddTour = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const caps = usePollenCapabilities();
  // Profit / cost visibility — STRICTLY Super Admin. Admin can still edit
  // vendor payments and customer price; just can't see aggregate cost
  // or profit numbers.
  const profitVisible = caps.is_super_admin;
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  // Customer dropdown visibility + ref for click-outside detection.
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const customerFieldRef = useRef(null);
  // Duplicate-tour confirmation modal state
  const [dupDialog, setDupDialog] = useState({ open: false, existing: null, payload: null });

  // Close the customer dropdown when clicking anywhere outside the input/list.
  useEffect(() => {
    if (!customerDropdownOpen) return;
    const handler = (e) => {
      if (customerFieldRef.current && !customerFieldRef.current.contains(e.target)) {
        setCustomerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [customerDropdownOpen]);

  const authHeaders = () => {
    const h = {};
    const t = localStorage.getItem('session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  };
  const authFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...authHeaders() } });
  const [pasteText, setPasteText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [tour, setTour] = useState({
    tour_name: '', source_type: 'Direct', customer_id: '', customer_name: '', destination: '', is_international: false,
    start_date: '', end_date: '', invoice_date: new Date().toISOString().slice(0, 10), adults: 1, children: 0,
    itinerary: [], hotels: [{ ...emptyHotel }], activities: [{ ...emptyActivity }], transport: [{ ...emptyTransport }],
    guides: [{ ...emptyGuide }],
    initiatives: [], care: [], other_expenses: [],
    agent_details: { ...emptyAgent }, inclusions: '', exclusions: '', customer_amount: 0, status: 'Planning', is_historical: false,
  });

  useEffect(() => {
    authFetch(`${POLLEN_API}/customers`).then(r => r.ok ? r.json() : []).then(setCustomers).catch(() => {});
    authFetch(`${POLLEN_API}/vendors`).then(r => r.ok ? r.json() : []).then(setVendors).catch(() => {});
  }, []);
  const [vendors, setVendors] = useState([]);
  const hotelVendors = vendors.filter(v => v.type === 'Hotel');
  const transportVendors = vendors.filter(v => v.type === 'Transport');
  const driverVendors = vendors.filter(v => v.type === 'Driver');
  const activityVendors = vendors.filter(v => v.type === 'Activity');
  const guideVendors = vendors.filter(v => v.type === 'Guide');
  const agentVendors = vendors.filter(v => v.type === 'Agent');

  // Agent details block surfaces for both Agent (full agent-led tour) AND
  // Mixed (operator handles part, agent handles part). The operator wants
  // those two source types to expose the agent fields above Hotels.
  const isAgent = tour.source_type === 'Agent' || tour.source_type === 'Mixed';

  const updateField = (field, value) => setTour(prev => ({ ...prev, [field]: value }));

  const updateListItem = (listName, index, field, value) => {
    setTour(prev => {
      const list = [...prev[listName]];
      list[index] = { ...list[index], [field]: value };
      if (listName === 'hotels' && (field === 'check_in' || field === 'check_out')) {
        const ci = field === 'check_in' ? value : list[index].check_in;
        const co = field === 'check_out' ? value : list[index].check_out;
        if (ci && co) { try { list[index].nights = Math.max(0, Math.round((new Date(co) - new Date(ci)) / 86400000)); } catch {} }
      }
      if (['total_amount', 'advance_paid', 'advance'].includes(field)) {
        const total = parseFloat(list[index].total_amount) || 0;
        const adv = parseFloat(list[index].advance_paid ?? list[index].advance) || 0;
        list[index].balance = total - adv;
      }
      return { ...prev, [listName]: list };
    });
  };

  const addListItem = (listName, template) => setTour(prev => ({ ...prev, [listName]: [...prev[listName], { ...template }] }));
  const removeListItem = (listName, index) => setTour(prev => ({ ...prev, [listName]: prev[listName].filter((_, i) => i !== index) }));

  const updateAgentField = (field, value) => {
    setTour(prev => {
      const ad = { ...prev.agent_details, [field]: value };
      if (['total_package_cost', 'advance_paid'].includes(field)) {
        ad.balance = (parseFloat(ad.total_package_cost) || 0) - (parseFloat(ad.advance_paid) || 0);
      }
      return { ...prev, agent_details: ad };
    });
  };

  const totalCost = ((tour.source_type === 'Agent' || tour.source_type === 'Mixed') ? (parseFloat(tour.agent_details.total_package_cost) || 0) : 0)
    + tour.hotels.reduce((s, h) => s + (parseFloat(h.total_amount) || 0), 0)
    + tour.activities.reduce((s, a) => s + (parseFloat(a.total_amount) || 0), 0)
    + tour.transport.reduce((s, t) => s + (parseFloat(t.total_amount) || 0), 0)
    + tour.guides.reduce((s, g) => s + (parseFloat(g.total_amount) || 0), 0)
    + tour.initiatives.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
    + tour.care.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0)
    + tour.other_expenses.reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
  const grossProfit = (parseFloat(tour.customer_amount) || 0) - totalCost;

  const generateItinerary = () => {
    if (!tour.start_date || !tour.end_date) return;
    try {
      const days = eachDayOfInterval({ start: parseISO(tour.start_date), end: parseISO(tour.end_date) });
      // Build lookup from extracted itinerary (by date or day_number) + existing user-edited entries
      const extracted = tour._extracted_itinerary || [];
      const existing = tour.itinerary || [];
      const itin = days.map((d, i) => {
        const dateStr = format(d, 'yyyy-MM-dd');
        const ex = extracted.find(e => e.date === dateStr || e.day_number === i + 1);
        const keep = existing.find(e => e.date === dateStr || e.day_number === i + 1);
        return {
          day_number: i + 1,
          date: dateStr,
          weekday: format(d, 'EEEE'),
          overnight_location: (ex && ex.overnight_location) || (keep && keep.overnight_location) || ''
        };
      });
      updateField('itinerary', itin);
    } catch {}
  };

  useEffect(() => { generateItinerary(); }, [tour.start_date, tour.end_date, tour._extracted_itinerary]);

  const matchCustomer = (name) => {
    if (!name) return { customer_name: '', customer_id: '' };
    // Normalize: lowercase, strip punctuation, collapse whitespace — same rules as backend.
    const norm = (s) => (s || '').toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
    const target = norm(name);
    // Only exact normalized match auto-selects. Anything else leaves it to the user via the dropdown.
    const exact = customers.find(c => norm(c.name) === target);
    if (exact) return { customer_name: exact.name, customer_id: exact.id };
    return { customer_name: name, customer_id: '' };
  };

  const handleExtract = async () => {
    if (!pasteText.trim()) { toast.error('Please paste some text first'); return; }
    setExtracting(true);
    try {
      const res = await authFetch(`${POLLEN_API}/extract-text`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: pasteText }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.error) { toast.error(d.error); setExtracting(false); return; }
        const cm = matchCustomer(d.customer_name);
        setTour(prev => ({
          ...prev,
          customer_name: cm.customer_name || prev.customer_name,
          customer_id: cm.customer_id || prev.customer_id,
          destination: d.destination || prev.destination,
          start_date: d.start_date || prev.start_date,
          end_date: d.end_date || prev.end_date,
          adults: d.adults || prev.adults,
          children: d.children || prev.children,
          source_type: d.source_type || prev.source_type,
          hotels: (d.hotels && d.hotels.length > 0) ? d.hotels.map(h => ({
            ...emptyHotel, ...h,
            nights: h.check_in && h.check_out ? Math.max(0, Math.round((new Date(h.check_out) - new Date(h.check_in)) / 86400000)) : 0,
            balance: (parseFloat(h.total_amount) || 0) - (parseFloat(h.advance_paid) || 0),
          })) : prev.hotels,
          activities: (d.activities && d.activities.length > 0) ? d.activities.map(a => ({ ...emptyActivity, ...a, balance: (parseFloat(a.total_amount) || 0) - (parseFloat(a.advance) || 0) })) : prev.activities,
          transport: (d.transport && d.transport.length > 0) ? d.transport.map(t => ({ ...emptyTransport, ...t, balance: (parseFloat(t.total_amount) || 0) - (parseFloat(t.advance) || 0) })) : prev.transport,
          inclusions: d.inclusions || prev.inclusions,
          exclusions: d.exclusions || prev.exclusions,
          customer_amount: d.customer_amount || prev.customer_amount,
          _extracted_itinerary: d.itinerary || [],
        }));
        toast.success('Data extracted! Review and edit below.');
        setPasteText('');
      } else { const err = await res.json(); toast.error(err.detail || 'Extraction failed'); }
    } catch (e) { toast.error('Error during extraction'); }
    setExtracting(false);
  };

  // Core POST logic, extracted so it can be re-invoked with ?force=true after user confirms duplicate.
  const submitTour = async (payload, { force = false } = {}) => {
    const url = `${POLLEN_API}/tours${force ? '?force=true' : ''}`;
    const res = await authFetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const createdTour = await res.json();
      if (pendingFiles.length > 0) {
        for (const pf of pendingFiles) {
          const fd = new FormData();
          fd.append('file', pf.file);
          fd.append('tour_id', createdTour.id);
          fd.append('category', pf.category || 'Document');
          await authFetch(`${POLLEN_API}/files/upload`, { method: 'POST', body: fd });
        }
      }
      toast.success(force ? 'Tour created (duplicate allowed)' : 'Tour created successfully');
      navigate('/pollen-dots-dashboard');
      return true;
    }
    if (res.status === 409) {
      // Duplicate-tour detected by backend — surface a confirm dialog instead of a silent failure.
      const err = await res.json();
      const detail = err?.detail;
      if (detail?.code === 'duplicate_tour' && detail?.existing_tour) {
        setDupDialog({ open: true, existing: detail.existing_tour, payload });
        return false;
      }
    }
    const err = await res.json().catch(() => ({}));
    toast.error(err.detail || 'Failed to create tour');
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tour.customer_name && !tour.customer_id) { toast.error('Please enter a customer name'); return; }
    if (!tour.start_date || !tour.end_date) { toast.error('Please enter tour dates'); return; }
    if (!tour.invoice_date) { toast.error('Please enter an invoice date \u2014 it decides which fiscal year this tour belongs to'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...tour,
        hotels: tour.hotels.filter(h => h.hotel_name),
        activities: tour.activities.filter(a => a.activity_name),
        transport: tour.transport.filter(t => t.vehicle_type || t.route),
        guides: tour.guides.filter(g => g.guide_name),
        agent_details: isAgent ? tour.agent_details : null,
        customer_amount: parseFloat(tour.customer_amount) || 0,
      };
      delete payload._extracted_itinerary;
      await submitTour(payload);
    } catch (e) { toast.error('Error creating tour'); }
    setSubmitting(false);
  };

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="add-tour-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/pollen-dots/tours'); }} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Add New Tour</h1>
            <p className="text-muted-foreground text-sm">Create a new trip booking</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* AI Extraction + File Upload */}
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-600" />Quick Fill — Paste Text or Upload File</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">Upload a document or paste text — AI will extract tour data automatically.</p>

              {/* File Upload Area */}
              <div className="border-2 border-dashed border-amber-300 rounded-lg p-4 text-center space-y-2 bg-white/50">
                <Upload className="w-8 h-8 text-amber-500 mx-auto" />
                <p className="text-sm font-medium">Upload Itinerary / Invoice</p>
                <p className="text-xs text-muted-foreground">PDF, DOC, Excel, CSV, TXT supported</p>
                <label className="cursor-pointer inline-block">
                  <input type="file" className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.txt"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      // Default new uploads to Invoice (most common case on the Add Tour page).
                      // User can change per-file via the Select dropdown below.
                      if (file) setPendingFiles(prev => [...prev, { file, category: 'Invoice' }]);
                      e.target.value = '';
                    }}
                    data-testid="tour-file-upload-top" />
                  <Button type="button" variant="outline" size="sm" asChild className="border-amber-300 hover:bg-amber-100">
                    <span><Upload className="w-4 h-4 mr-1" />Choose File</span>
                  </Button>
                </label>
              </div>

              {/* Show pending files with Extract button */}
              {pendingFiles.length > 0 && (
                <div className="space-y-2">
                  {pendingFiles.map((pf, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg bg-white border border-amber-200 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                        <div className="min-w-0">
                          <span className="font-medium truncate block">{pf.file.name}</span>
                          <span className="text-xs text-muted-foreground">{(pf.file.size / 1024).toFixed(1)} KB</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Per-file category tag (Invoice | Itinerary | Document) */}
                        <Select value={pf.category || 'Document'}
                          onValueChange={(v) => setPendingFiles(prev => prev.map((x, idx) => idx === i ? { ...x, category: v } : x))}>
                          <SelectTrigger className="h-7 text-xs w-[110px]" data-testid={`pending-file-cat-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Invoice">Invoice</SelectItem>
                            <SelectItem value="Itinerary">Itinerary</SelectItem>
                            <SelectItem value="Document">Document</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button type="button" size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                          disabled={extracting}
                          onClick={async () => {
                            setExtracting(true);
                            const fd = new FormData(); fd.append('file', pf.file);
                            try {
                              const res = await authFetch(`${POLLEN_API}/extract-file`, { method: 'POST', body: fd });
                              if (res.ok) {
                                const d = await res.json();
                                if (d.error) { toast.error(d.error); } else {
                                  const cm = matchCustomer(d.customer_name);
                                  setTour(prev => ({
                                    ...prev,
                                    customer_name: cm.customer_name || prev.customer_name,
                                    customer_id: cm.customer_id || prev.customer_id,
                                    destination: d.destination || prev.destination,
                                    start_date: d.start_date || prev.start_date,
                                    end_date: d.end_date || prev.end_date,
                                    adults: d.adults || prev.adults,
                                    children: d.children || prev.children,
                                    source_type: d.source_type || prev.source_type,
                                    hotels: d.hotels?.length ? d.hotels.map(h => ({ ...emptyHotel, ...h, nights: h.check_in && h.check_out ? Math.max(0, Math.round((new Date(h.check_out) - new Date(h.check_in)) / 86400000)) : 0, balance: (parseFloat(h.total_amount) || 0) - (parseFloat(h.advance_paid) || 0) })) : prev.hotels,
                                    activities: d.activities?.length ? d.activities.map(a => ({ ...emptyActivity, ...a })) : prev.activities,
                                    transport: d.transport?.length ? d.transport.map(t => ({ ...emptyTransport, ...t })) : prev.transport,
                                    inclusions: d.inclusions || prev.inclusions,
                                    exclusions: d.exclusions || prev.exclusions,
                                    customer_amount: d.customer_amount || prev.customer_amount,
                                  }));
                                  toast.success('Data extracted from file! Review below.');
                                }
                              } else { const err = await res.json(); toast.error(err.detail || 'Extraction failed'); }
                            } catch (err) { toast.error('Extraction error'); }
                            setExtracting(false);
                          }}
                          data-testid={`extract-file-${i}`}>
                          {extracting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                          Extract
                        </Button>
                        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive"
                          onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}>
                          <XIcon className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-amber-700">{pendingFiles.length} file(s) will be attached when you save the tour</p>
                </div>
              )}

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-amber-200" />
                <span className="text-xs text-amber-600 font-medium">OR PASTE TEXT</span>
                <div className="flex-1 border-t border-amber-200" />
              </div>

              {/* Text Paste */}
              <Textarea className="text-sm min-h-[80px]" placeholder="Paste your itinerary or invoice text here..." value={pasteText} onChange={e => setPasteText(e.target.value)} data-testid="paste-text-input" />
              <Button type="button" variant="outline" size="sm" onClick={handleExtract} disabled={extracting || !pasteText.trim()} data-testid="extract-btn" className="border-amber-300 hover:bg-amber-100">
                {extracting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ClipboardPaste className="w-4 h-4 mr-1" />}
                {extracting ? 'Extracting...' : 'Extract Data'}
              </Button>
            </CardContent>
          </Card>

          {/* Source Type */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Source Type</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {['Direct', 'OTA', 'Mixed', 'Agent'].map(s => (
                  <Button key={s} type="button" variant={tour.source_type === s ? 'default' : 'outline'} size="sm"
                    onClick={() => updateField('source_type', s)} data-testid={`source-${s.toLowerCase()}`}>{s}</Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Customer & Tour Details */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><UserCheck className="w-4 h-4" />Customer & Trip Details</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative" ref={customerFieldRef}>
                  <Label>Customer *</Label>
                  <Input value={tour.customer_name}
                    onChange={e => { updateField('customer_name', e.target.value); updateField('customer_id', ''); setCustomerDropdownOpen(true); }}
                    onFocus={() => setCustomerDropdownOpen(true)}
                    placeholder="Search or type customer name"
                    data-testid="input-customer-name"
                    autoComplete="off"
                    required />
                  {customerDropdownOpen && !tour.customer_id && (() => {
                    const q = (tour.customer_name || '').trim().toLowerCase();
                    const matches = q
                      ? customers.filter(c => (c.name || '').toLowerCase().includes(q)).slice(0, 8)
                      : [];
                    const showAddNew = q.length > 0;
                    if (matches.length === 0 && !showAddNew) return null;
                    return (
                      <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="customer-dropdown">
                        {matches.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/50 border-b">
                              Existing customers
                            </div>
                            {matches.map(c => (
                              <button key={c.id} type="button"
                                className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between items-center gap-2"
                                onClick={() => {
                                  updateField('customer_name', c.name);
                                  updateField('customer_id', c.id);
                                  setCustomerDropdownOpen(false);
                                }}
                                data-testid={`customer-option-${c.id}`}>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate">{c.name}</div>
                                  {(c.phone || c.email) && (
                                    <div className="text-xs text-muted-foreground truncate">
                                      {c.phone && <span>{c.phone}</span>}
                                      {c.phone && c.email && <span> · </span>}
                                      {c.email && <span>{c.email}</span>}
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                        {showAddNew && (
                          <button type="button"
                            className="w-full text-left px-3 py-2 border-t text-sm flex items-center gap-2 hover:bg-emerald-50 text-emerald-700 font-medium"
                            onClick={() => {
                              // Keep typed name, leave customer_id blank → backend creates a new customer on save.
                              updateField('customer_id', '');
                              setCustomerDropdownOpen(false);
                              toast.success('New customer will be created on save');
                            }}
                            data-testid="customer-option-add-new">
                            <Plus className="w-4 h-4" />
                            <span>Add new customer: <span className="font-semibold">"{tour.customer_name}"</span></span>
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {tour.customer_id && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid="existing-customer-hint">
                      Existing customer linked
                      <button type="button" className="underline text-muted-foreground ml-2"
                        onClick={() => { updateField('customer_id', ''); setCustomerDropdownOpen(true); }}
                        data-testid="change-customer-btn">
                        change
                      </button>
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Destination</Label>
                    {tour.destination && <LocationPinBtn locationName={tour.destination} />}
                  </div>
                  <LocationAutocomplete value={tour.destination} onChange={v => updateField('destination', v)}
                    type="city" placeholder="e.g., Goa, Kerala" testId="input-destination" />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer select-none" data-testid="toggle-international-wrap">
                    <input type="checkbox" className="h-4 w-4 rounded accent-primary"
                      checked={!!tour.is_international}
                      onChange={e => updateField('is_international', e.target.checked)}
                      data-testid="toggle-international" />
                    <span className="text-xs text-muted-foreground">
                      🌍 International tour — needed for destinations outside South Asia so the map can pin correctly
                    </span>
                  </label>
                </div>
                <div>
                  <Label>Start Date *</Label>
                  <Input type="date" value={tour.start_date} onChange={e => updateField('start_date', e.target.value)}
                    data-testid="input-start-date" required />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input type="date" value={tour.end_date} onChange={e => updateField('end_date', e.target.value)}
                    data-testid="input-end-date" required />
                </div>
                <div>
                  <Label>Invoice Date *</Label>
                  <Input type="date" value={tour.invoice_date} onChange={e => updateField('invoice_date', e.target.value)}
                    data-testid="input-invoice-date" required />
                  <p className="text-[10px] text-muted-foreground mt-1">Date invoice was raised (used for FY revenue bucketing)</p>
                </div>
                <div>
                  <Label>Adults</Label>
                  <Input type="number" min="1" value={tour.adults} onChange={e => updateField('adults', parseInt(e.target.value) || 1)}
                    data-testid="input-adults" />
                </div>
                <div>
                  <Label>Children</Label>
                  <Input type="number" min="0" value={tour.children} onChange={e => updateField('children', parseInt(e.target.value) || 0)}
                    data-testid="input-children" />
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={tour.status} onValueChange={v => updateField('status', v)}>
                  <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Planning', 'Booked', 'Ongoing', 'Completed', 'Cancelled'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-start gap-2 p-2 rounded-lg border border-amber-200 bg-amber-50/60">
                <input
                  type="checkbox"
                  id="add-is-historical-toggle"
                  className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer"
                  checked={!!tour.is_historical}
                  onChange={e => updateField('is_historical', e.target.checked)}
                  data-testid="add-is-historical-checkbox"
                />
                <label htmlFor="add-is-historical-toggle" className="text-xs leading-tight cursor-pointer">
                  <span className="font-semibold text-amber-900">Mark as Historical</span>
                  <span className="block text-[11px] text-amber-800/80 mt-0.5">
                    Excludes this tour from <em>Current Year</em> and <em>Last Year</em> dashboard KPIs. Still counted in Lifetime. Use this for backfilled trips from prior FYs.
                  </span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Day-wise Itinerary */}
          {tour.itinerary.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4" />Day-wise Itinerary</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {tour.itinerary.map((day, i) => (
                    <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2 text-sm">
                      <Badge variant="outline" className="min-w-[55px] justify-center text-xs">Day {day.day_number}</Badge>
                      <span className="text-muted-foreground text-xs">{formatDate(day.date)}</span>
                      <span className="text-muted-foreground text-xs">{day.weekday?.slice(0, 3)}</span>
                      {/* Mobile: drop location field onto its own line so it
                          stays full-width and readable. Desktop: keep the
                          5-element row. */}
                      <div className="flex items-center gap-1 w-full sm:flex-1 sm:w-auto basis-full sm:basis-0">
                        <LocationAutocomplete value={day.overnight_location} type="city" placeholder="Overnight location"
                          className="h-7 text-xs flex-1 min-w-0"
                          onChange={v => {
                            const itin = [...tour.itinerary];
                            itin[i] = { ...itin[i], overnight_location: v };
                            updateField('itinerary', itin);
                          }}
                          onSelect={loc => {
                            // Picked an existing saved city — carry its coords
                            // forward so save doesn't re-create a duplicate.
                            const itin = [...tour.itinerary];
                            itin[i] = {
                              ...itin[i],
                              overnight_location: loc.name,
                              overnight_lat: loc.lat,
                              overnight_lng: loc.lng,
                              overnight_location_id: loc.id,
                            };
                            updateField('itinerary', itin);
                          }} />
                        {day.overnight_location && <LocationPinBtn locationName={day.overnight_location} />}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Agent Details — surfaced for Agent + Mixed source types. Placed
              ABOVE Hotels because operators want to capture which agent is
              brokering before filling out hotels/activities. */}
          {isAgent && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><UserCheck className="w-4 h-4" />Agent Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><Label className="text-xs">Agency Name</Label><VendorAutocomplete value={tour.agent_details.agency_name} vendors={agentVendors} placeholder="Type or select agent"
                    onChange={v => updateAgentField('agency_name', v)}
                    onSelect={v => { updateAgentField('agency_name', v.name); if (v.contact_name) updateAgentField('contact_name', v.contact_name); if (v.phone) updateAgentField('contact_number', v.phone); }}
                    testId="agent-agency" /></div>
                  <div><Label className="text-xs">Contact Name</Label><Input className="h-8 text-sm" value={tour.agent_details.contact_name} onChange={e => updateAgentField('contact_name', e.target.value)} data-testid="agent-contact" /></div>
                  <div><Label className="text-xs">Contact Number</Label><Input className="h-8 text-sm" value={tour.agent_details.contact_number} onChange={e => updateAgentField('contact_number', e.target.value)} data-testid="agent-phone" /></div>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                  <div><Label className="text-xs">Total Package Cost</Label><Input type="number" className="h-8 text-sm" value={tour.agent_details.total_package_cost} onChange={e => updateAgentField('total_package_cost', e.target.value)} data-testid="agent-total" /></div>
                  <div><Label className="text-xs">Advance Paid</Label><Input type="number" className="h-8 text-sm" value={tour.agent_details.advance_paid} onChange={e => updateAgentField('advance_paid', e.target.value)} data-testid="agent-advance" /></div>
                  <div><Label className="text-xs">Balance</Label><Input className="h-8 text-sm bg-muted" value={tour.agent_details.balance} readOnly /></div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hotels */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Bed className="w-4 h-4" />Hotels</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => addListItem('hotels', emptyHotel)} data-testid="add-hotel-btn">
                  <Plus className="w-3 h-3 mr-1" />Add Hotel
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {tour.hotels.map((h, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-3 relative">
                  {tour.hotels.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-destructive h-6 w-6 p-0"
                      onClick={() => removeListItem('hotels', i)}><Trash2 className="w-3 h-3" /></Button>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div><Label className="text-xs">Hotel Name</Label><VendorAutocomplete value={h.hotel_name} vendors={hotelVendors} placeholder="Type or select hotel"
                      onChange={v => updateListItem('hotels', i, 'hotel_name', v)}
                      onSelect={v => { updateListItem('hotels', i, 'hotel_name', v.name); if (v.contact_name) updateListItem('hotels', i, 'contact_name', v.contact_name); if (v.phone) updateListItem('hotels', i, 'contact_number', v.phone); }}
                      testId={`hotel-name-${i}`} /></div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Location</Label>
                        {h.location && <LocationPinBtn locationName={h.location} />}
                      </div>
                      <LocationAutocomplete value={h.location} type="city" className="h-8 text-sm"
                        onChange={v => updateListItem('hotels', i, 'location', v)}
                        onSelect={async (loc) => {
                          updateListItem('hotels', i, 'location', loc.name);
                          const r = await inheritCityPin({ cityLoc: loc, entityName: h.hotel_name, entityType: 'hotel' });
                          if (r.already_pinned) toast.info(`"${h.hotel_name}" already has its own map pin — kept it.`);
                          else if (r.inherited) toast.success(`Inherited ${loc.name} city pin onto "${h.hotel_name}" (approximate) — paste its Google Maps URL to pin the exact spot.`);
                        }} />
                    </div>
                    {h.hotel_name && (
                      <div className="sm:col-span-2">
                        <HotelMapUrlField
                          entityName={h.hotel_name}
                          entityType="hotel"
                          city={h.location}
                          initialUrl={h.maps_url}
                          initialLat={h.lat}
                          initialLng={h.lng}
                          initialVerified={!!h.maps_url}
                          onPinned={(p) => {
                            updateListItem('hotels', i, 'maps_url', p.url);
                            updateListItem('hotels', i, 'lat', p.lat);
                            updateListItem('hotels', i, 'lng', p.lng);
                          }}
                          compact
                          label="Google Maps URL (paste link to pin exact spot)"
                        />
                      </div>
                    )}
                    <div><Label className="text-xs">Check-in</Label><Input type="date" className="h-8 text-sm" value={h.check_in} onChange={e => updateListItem('hotels', i, 'check_in', e.target.value)} data-testid={`hotel-checkin-${i}`} /></div>
                    <div><Label className="text-xs">Check-out</Label><Input type="date" className="h-8 text-sm" value={h.check_out} onChange={e => updateListItem('hotels', i, 'check_out', e.target.value)} data-testid={`hotel-checkout-${i}`} /></div>
                    <div><Label className="text-xs">Nights</Label><Input className="h-8 text-sm bg-muted" value={h.nights} readOnly /></div>
                    <div><Label className="text-xs">Room Type</Label><Input className="h-8 text-sm" value={h.room_type} onChange={e => updateListItem('hotels', i, 'room_type', e.target.value)} /></div>
                    <div><Label className="text-xs">Meal Plan</Label><Input className="h-8 text-sm" value={h.meal_plan} onChange={e => updateListItem('hotels', i, 'meal_plan', e.target.value)} placeholder="e.g., MAP, AP" /></div>
                    <div><Label className="text-xs">Booking Status</Label>
                      <Select value={h.booking_status} onValueChange={v => updateListItem('hotels', i, 'booking_status', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Booked">Booked</SelectItem><SelectItem value="Confirmed">Confirmed</SelectItem></SelectContent>
                      </Select></div>
                    <div><Label className="text-xs">Contact Name</Label><Input className="h-8 text-sm" value={h.contact_name} onChange={e => updateListItem('hotels', i, 'contact_name', e.target.value)} /></div>
                    <div><Label className="text-xs">Contact Number</Label><Input className="h-8 text-sm" value={h.contact_number} onChange={e => updateListItem('hotels', i, 'contact_number', e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                    <div><Label className="text-xs">Total Amount</Label><Input type="number" className="h-8 text-sm" value={h.total_amount} onChange={e => updateListItem('hotels', i, 'total_amount', e.target.value)} /></div>
                    <div><Label className="text-xs">Advance Paid</Label><Input type="number" className="h-8 text-sm" value={h.advance_paid} onChange={e => updateListItem('hotels', i, 'advance_paid', e.target.value)} /></div>
                    <div><Label className="text-xs">Balance</Label><Input className="h-8 text-sm bg-muted" value={h.balance} readOnly /></div>
                  </div>
                  <div><Label className="text-xs">Special Notes</Label><Input className="h-8 text-sm" value={h.special_notes} onChange={e => updateListItem('hotels', i, 'special_notes', e.target.value)} /></div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Activities */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Compass className="w-4 h-4" />Activities</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => addListItem('activities', emptyActivity)} data-testid="add-activity-btn">
                  <Plus className="w-3 h-3 mr-1" />Add Activity
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {tour.activities.map((a, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2 relative">
                  {tour.activities.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-destructive h-6 w-6 p-0"
                      onClick={() => removeListItem('activities', i)}><Trash2 className="w-3 h-3" /></Button>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div><Label className="text-xs">Activity Name</Label><VendorAutocomplete value={a.activity_name} vendors={activityVendors} placeholder="Type or select activity"
                      onChange={v => updateListItem('activities', i, 'activity_name', v)}
                      onSelect={v => { updateListItem('activities', i, 'activity_name', v.name); if (v.address && !a.contact_details) updateListItem('activities', i, 'contact_details', v.address); if (v.location && !a.location) updateListItem('activities', i, 'location', v.location); }}
                      testId={`activity-name-${i}`} /></div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Location</Label>
                        {a.location && <LocationPinBtn locationName={a.location} />}
                      </div>
                      <LocationAutocomplete value={a.location || ''} type="city" className="h-8 text-sm"
                        onChange={v => updateListItem('activities', i, 'location', v)}
                        onSelect={async (loc) => {
                          updateListItem('activities', i, 'location', loc.name);
                          const r = await inheritCityPin({ cityLoc: loc, entityName: a.activity_name, entityType: 'activity' });
                          if (r.inherited) toast.success(`Inherited ${loc.name} pin onto "${a.activity_name}".`);
                        }} placeholder="e.g. Jaipur, Paro" testId={`activity-location-${i}`} />
                    </div>
                    {a.activity_name && (
                      <div className="sm:col-span-2">
                        <HotelMapUrlField
                          entityName={a.activity_name}
                          entityType="activity"
                          city={a.location}
                          initialUrl={a.maps_url}
                          initialLat={a.lat}
                          initialLng={a.lng}
                          initialVerified={!!a.maps_url}
                          onPinned={(p) => {
                            updateListItem('activities', i, 'maps_url', p.url);
                            updateListItem('activities', i, 'lat', p.lat);
                            updateListItem('activities', i, 'lng', p.lng);
                          }}
                          compact
                          label="Google Maps URL (paste link to pin exact spot)"
                        />
                      </div>
                    )}
                    <div><Label className="text-xs">Date</Label><Input type="date" className="h-8 text-sm" value={a.date} onChange={e => updateListItem('activities', i, 'date', e.target.value)} data-testid={`activity-date-${i}`} /></div>
                    <div><Label className="text-xs">Time Slot</Label><Input className="h-8 text-sm" value={a.time_slot} onChange={e => updateListItem('activities', i, 'time_slot', e.target.value)} placeholder="e.g., 9 AM - 12 PM" /></div>
                    <div><Label className="text-xs">Contact</Label><Input className="h-8 text-sm" value={a.contact_details} onChange={e => updateListItem('activities', i, 'contact_details', e.target.value)} /></div>
                    <div><Label className="text-xs">Status</Label>
                      <Select value={a.status} onValueChange={v => updateListItem('activities', i, 'status', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Booked">Booked</SelectItem><SelectItem value="Confirmed">Confirmed</SelectItem></SelectContent>
                      </Select></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                    <div><Label className="text-xs">Total</Label><Input type="number" className="h-8 text-sm" value={a.total_amount} onChange={e => updateListItem('activities', i, 'total_amount', e.target.value)} /></div>
                    <div><Label className="text-xs">Advance</Label><Input type="number" className="h-8 text-sm" value={a.advance} onChange={e => updateListItem('activities', i, 'advance', e.target.value)} /></div>
                    <div><Label className="text-xs">Balance</Label><Input className="h-8 text-sm bg-muted" value={a.balance} readOnly /></div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Transport */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Bus className="w-4 h-4" />Transport</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => addListItem('transport', emptyTransport)} data-testid="add-transport-btn">
                  <Plus className="w-3 h-3 mr-1" />Add Transport
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {tour.transport.map((t, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2 relative">
                  {tour.transport.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-destructive h-6 w-6 p-0"
                      onClick={() => removeListItem('transport', i)}><Trash2 className="w-3 h-3" /></Button>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div><Label className="text-xs">Vehicle Type</Label><Input className="h-8 text-sm" value={t.vehicle_type} onChange={e => updateListItem('transport', i, 'vehicle_type', e.target.value)} data-testid={`transport-type-${i}`} placeholder="e.g., Sedan, SUV, Tempo" /></div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Route / Sector</Label>
                        {t.route && <LocationPinBtn locationName={t.route} />}
                      </div>
                      <LocationAutocomplete value={t.route} type="route" className="h-8 text-sm"
                        onChange={v => updateListItem('transport', i, 'route', v)} testId={`transport-route-${i}`} />
                    </div>
                    <div><Label className="text-xs">Start Date</Label><Input type="date" className="h-8 text-sm" value={t.start_date} onChange={e => updateListItem('transport', i, 'start_date', e.target.value)} data-testid={`transport-start-${i}`} /></div>
                    <div><Label className="text-xs">End Date</Label><Input type="date" className="h-8 text-sm" value={t.end_date} onChange={e => updateListItem('transport', i, 'end_date', e.target.value)} data-testid={`transport-end-${i}`} /></div>
                    <div><Label className="text-xs">Operator Name</Label><VendorAutocomplete value={t.operator_name} vendors={transportVendors} placeholder="Type or select operator"
                      onChange={v => updateListItem('transport', i, 'operator_name', v)}
                      onSelect={v => { updateListItem('transport', i, 'operator_name', v.name); if (v.phone) updateListItem('transport', i, 'operator_contact', v.phone); }}
                      /></div>
                    <div><Label className="text-xs">Operator Contact</Label><Input className="h-8 text-sm" value={t.operator_contact} onChange={e => updateListItem('transport', i, 'operator_contact', e.target.value)} /></div>
                    <div><Label className="text-xs">Driver Name</Label><VendorAutocomplete value={t.driver_name} vendors={driverVendors} placeholder="Type or select driver"
                      onChange={v => updateListItem('transport', i, 'driver_name', v)}
                      onSelect={v => { updateListItem('transport', i, 'driver_name', v.name); if (v.phone) updateListItem('transport', i, 'driver_contact', v.phone); }}
                      /></div>
                    <div><Label className="text-xs">Driver Contact</Label><Input className="h-8 text-sm" value={t.driver_contact} onChange={e => updateListItem('transport', i, 'driver_contact', e.target.value)} /></div>
                    <div><Label className="text-xs">Status</Label>
                      <Select value={t.status || 'Pending'} onValueChange={v => updateListItem('transport', i, 'status', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Booked">Booked</SelectItem><SelectItem value="Confirmed">Confirmed</SelectItem></SelectContent>
                      </Select></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                    <div><Label className="text-xs">Total</Label><Input type="number" className="h-8 text-sm" value={t.total_amount} onChange={e => updateListItem('transport', i, 'total_amount', e.target.value)} /></div>
                    <div><Label className="text-xs">Advance</Label><Input type="number" className="h-8 text-sm" value={t.advance} onChange={e => updateListItem('transport', i, 'advance', e.target.value)} /></div>
                    <div><Label className="text-xs">Balance</Label><Input className="h-8 text-sm bg-muted" value={t.balance} readOnly /></div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Guides */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><MapPin className="w-4 h-4" />Guides</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => addListItem('guides', emptyGuide)} data-testid="add-guide-btn">
                  <Plus className="w-3 h-3 mr-1" />Add Guide
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {tour.guides.map((g, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2 relative">
                  {tour.guides.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-destructive h-6 w-6 p-0"
                      onClick={() => removeListItem('guides', i)}><Trash2 className="w-3 h-3" /></Button>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div><Label className="text-xs">Guide Name</Label><VendorAutocomplete value={g.guide_name} vendors={guideVendors} placeholder="Type or select guide"
                      onChange={v => updateListItem('guides', i, 'guide_name', v)}
                      onSelect={v => { updateListItem('guides', i, 'guide_name', v.name); if (v.phone) updateListItem('guides', i, 'phone', v.phone); if (v.email) updateListItem('guides', i, 'email', v.email); if (v.address && !g.location) updateListItem('guides', i, 'location', v.address); }}
                      testId={`guide-name-${i}`} /></div>
                    <div><Label className="text-xs">Phone</Label><Input className="h-8 text-sm" value={g.phone} onChange={e => updateListItem('guides', i, 'phone', e.target.value)} data-testid={`guide-phone-${i}`} /></div>
                    <div><Label className="text-xs">Email</Label><Input className="h-8 text-sm" value={g.email} onChange={e => updateListItem('guides', i, 'email', e.target.value)} /></div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Location / Area</Label>
                        {g.location && <LocationPinBtn locationName={g.location} />}
                      </div>
                      <LocationAutocomplete value={g.location} type="city" className="h-8 text-sm" placeholder="e.g., Jaipur, Kerala"
                        onChange={v => updateListItem('guides', i, 'location', v)}
                        onSelect={async (loc) => {
                          updateListItem('guides', i, 'location', loc.name);
                          const r = await inheritCityPin({ cityLoc: loc, entityName: g.guide_name, entityType: 'guide' });
                          if (r.inherited) toast.success(`Inherited ${loc.name} pin onto "${g.guide_name}".`);
                        }} />
                    </div>
                    <div><Label className="text-xs">Start Date</Label><Input type="date" className="h-8 text-sm" value={g.start_date} onChange={e => updateListItem('guides', i, 'start_date', e.target.value)} data-testid={`guide-start-${i}`} /></div>
                    <div><Label className="text-xs">End Date</Label><Input type="date" className="h-8 text-sm" value={g.end_date} onChange={e => updateListItem('guides', i, 'end_date', e.target.value)} data-testid={`guide-end-${i}`} /></div>
                    <div><Label className="text-xs">Status</Label>
                      <Select value={g.status || 'Pending'} onValueChange={v => updateListItem('guides', i, 'status', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Booked">Booked</SelectItem><SelectItem value="Confirmed">Confirmed</SelectItem></SelectContent>
                      </Select></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                    <div><Label className="text-xs">Total Amount</Label><Input type="number" className="h-8 text-sm" value={g.total_amount} onChange={e => updateListItem('guides', i, 'total_amount', e.target.value)} /></div>
                    <div><Label className="text-xs">Advance</Label><Input type="number" className="h-8 text-sm" value={g.advance} onChange={e => updateListItem('guides', i, 'advance', e.target.value)} /></div>
                    <div><Label className="text-xs">Balance</Label><Input className="h-8 text-sm bg-muted" value={g.balance} readOnly /></div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Initiatives */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Leaf className="w-4 h-4 text-emerald-600" />Initiatives ({tour.initiatives.length})</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => addListItem('initiatives', { subtype: 'Ambassador of Smiles', description: '', amount: 0 })} data-testid="add-initiative-btn"><Plus className="w-3 h-3 mr-1" />Add Initiative</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {tour.initiatives.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">Optional — track CSR/impact expenses on this tour</p>}
              {tour.initiatives.map((it, i) => (
                <div key={i} className="p-2 border rounded grid grid-cols-1 sm:grid-cols-4 gap-2 relative">
                  <Button type="button" variant="ghost" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0 text-destructive" onClick={() => removeListItem('initiatives', i)}><Trash2 className="w-3 h-3" /></Button>
                  <div><Label className="text-xs">Initiative</Label>
                    <Select value={it.subtype} onValueChange={v => updateListItem('initiatives', i, 'subtype', v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ambassador of Smiles">Ambassador of Smiles</SelectItem>
                        <SelectItem value="Architects of Cultural Exchange">Architects of Cultural Exchange</SelectItem>
                        <SelectItem value="Stewards of Sustainability">Stewards of Sustainability</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2 pr-6"><Label className="text-xs">Description</Label><Input className="h-8 text-sm" value={it.description} onChange={e => updateListItem('initiatives', i, 'description', e.target.value)} placeholder="What was done / recipient" /></div>
                  <div><Label className="text-xs">Amount</Label><Input type="number" className="h-8 text-sm" value={it.amount} onChange={e => updateListItem('initiatives', i, 'amount', e.target.value)} /></div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Care */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Heart className="w-4 h-4 text-rose-600" />Care ({tour.care.length})</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => addListItem('care', { beneficiary_type: 'Guest', description: '', amount: 0 })} data-testid="add-care-btn"><Plus className="w-3 h-3 mr-1" />Add Care</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {tour.care.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">Gratitudes for staff/drivers/guides/locals or complimentary guest experiences</p>}
              {tour.care.map((c, i) => (
                <div key={i} className="p-2 border rounded grid grid-cols-1 sm:grid-cols-4 gap-2 relative">
                  <Button type="button" variant="ghost" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0 text-destructive" onClick={() => removeListItem('care', i)}><Trash2 className="w-3 h-3" /></Button>
                  <div><Label className="text-xs">Beneficiary</Label>
                    <Select value={c.beneficiary_type} onValueChange={v => updateListItem('care', i, 'beneficiary_type', v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['Guest', 'Staff', 'Driver', 'Guide', 'Operator', 'Local Caregiver', 'Other'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2 pr-6"><Label className="text-xs">Description</Label><Input className="h-8 text-sm" value={c.description} onChange={e => updateListItem('care', i, 'description', e.target.value)} placeholder="Gift / gratitude / complimentary upgrade" /></div>
                  <div><Label className="text-xs">Amount</Label><Input type="number" className="h-8 text-sm" value={c.amount} onChange={e => updateListItem('care', i, 'amount', e.target.value)} /></div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Other Services */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Package className="w-4 h-4 text-slate-600" />Other Services ({tour.other_expenses.length})</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => addListItem('other_expenses', { sub_type: 'Misc Trip Expense', description: '', amount: 0 })} data-testid="add-other-btn"><Plus className="w-3 h-3 mr-1" />Add Other</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {tour.other_expenses.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">ILP, permits, miscellaneous trip expenses, upgrades</p>}
              {tour.other_expenses.map((o, i) => (
                <div key={i} className="p-2 border rounded grid grid-cols-1 sm:grid-cols-4 gap-2 relative">
                  <Button type="button" variant="ghost" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0 text-destructive" onClick={() => removeListItem('other_expenses', i)}><Trash2 className="w-3 h-3" /></Button>
                  <div><Label className="text-xs">Type</Label>
                    <Select value={o.sub_type} onValueChange={v => updateListItem('other_expenses', i, 'sub_type', v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['ILP', 'Permit', 'Misc Trip Expense', 'Upgrade', 'Other'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2 pr-6"><Label className="text-xs">Description</Label><Input className="h-8 text-sm" value={o.description} onChange={e => updateListItem('other_expenses', i, 'description', e.target.value)} /></div>
                  <div><Label className="text-xs">Amount</Label><Input type="number" className="h-8 text-sm" value={o.amount} onChange={e => updateListItem('other_expenses', i, 'amount', e.target.value)} /></div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Inclusions / Exclusions */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Inclusions / Exclusions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-xs">Inclusions</Label><Textarea className="text-sm" rows={2} value={tour.inclusions} onChange={e => updateField('inclusions', e.target.value)} placeholder="What's included..." data-testid="input-inclusions" /></div>
              <div><Label className="text-xs">Exclusions</Label><Textarea className="text-sm" rows={2} value={tour.exclusions} onChange={e => updateField('exclusions', e.target.value)} placeholder="What's not included..." data-testid="input-exclusions" /></div>
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><IndianRupee className="w-4 h-4" />Tour Financial Summary</CardTitle></CardHeader>
            <CardContent>
              <div className={`grid grid-cols-1 gap-3 ${profitVisible ? 'sm:grid-cols-3' : ''}`}>
                <div>
                  <Label className="text-xs">Customer Amount</Label>
                  <Input type="number" className="h-8 text-sm" value={tour.customer_amount} onChange={e => updateField('customer_amount', e.target.value)} data-testid="input-customer-amount" />
                </div>
                {profitVisible && (
                  <>
                    <div>
                      <Label className="text-xs">Total Cost</Label>
                      <div className="h-8 flex items-center px-3 bg-muted rounded-md text-sm font-medium" data-testid="total-cost">{totalCost.toLocaleString('en-IN')}</div>
                    </div>
                    <div>
                      <Label className="text-xs">Gross Profit</Label>
                      <div className={`h-8 flex items-center px-3 rounded-md text-sm font-bold ${grossProfit >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid="gross-profit">
                        {grossProfit.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-3 justify-end pb-8">
            <Button type="button" variant="outline" onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/pollen-dots/tours'); }}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="save-tour-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Tour
            </Button>
          </div>
        </form>

        {/* Duplicate tour confirmation dialog */}
        <Dialog open={dupDialog.open} onOpenChange={(o) => !o && setDupDialog({ open: false, existing: null, payload: null })}>
          <DialogContent className="max-w-md" data-testid="dup-tour-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />Duplicate tour detected
              </DialogTitle>
              <DialogDescription className="text-sm">
                A tour with the same customer and dates already exists.
              </DialogDescription>
            </DialogHeader>
            {dupDialog.existing && (
              <div className="rounded-md border bg-amber-50 p-3 text-sm space-y-1" data-testid="dup-tour-existing-info">
                <div className="font-medium">{dupDialog.existing.tour_name || 'Existing tour'}</div>
                <div className="text-xs text-muted-foreground">
                  {dupDialog.existing.destination || '—'} · {formatDate(dupDialog.existing.start_date)} → {formatDate(dupDialog.existing.end_date)}
                </div>
                {dupDialog.existing.customer_amount != null && (
                  <div className="text-xs text-muted-foreground">
                    Amount: ₹{Number(dupDialog.existing.customer_amount || 0).toLocaleString('en-IN')}
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="gap-2 sm:justify-end">
              <Button type="button" variant="outline"
                onClick={() => setDupDialog({ open: false, existing: null, payload: null })}
                data-testid="dup-cancel-btn">
                Cancel
              </Button>
              <Button type="button" variant="outline"
                onClick={() => {
                  const eid = dupDialog.existing?.id;
                  setDupDialog({ open: false, existing: null, payload: null });
                  if (eid) navigate(`/pollen-dots/tour/${eid}`);
                }}
                data-testid="dup-view-existing-btn">
                View Existing Tour
              </Button>
              <Button type="button"
                onClick={async () => {
                  const payload = dupDialog.payload;
                  setDupDialog({ open: false, existing: null, payload: null });
                  if (!payload) return;
                  setSubmitting(true);
                  await submitTour(payload, { force: true });
                  setSubmitting(false);
                }}
                data-testid="dup-create-anyway-btn">
                Create Anyway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const VendorAutocomplete = ({ value, onChange, vendors, placeholder, testId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const matches = value ? vendors.filter(v => v.name.toLowerCase().includes(value.toLowerCase())) : vendors;
  return (
    <div className="relative">
      <Input className="h-8 text-sm" value={value} placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        data-testid={testId} />
      {open && matches.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-y-auto">
          {matches.slice(0, 6).map(v => (
            <button key={v.id} type="button" className="w-full text-left px-3 py-1.5 hover:bg-muted text-xs"
              onMouseDown={e => { e.preventDefault(); onChange(v.name); if (onSelect) onSelect(v); setOpen(false); }}>
              <span className="font-medium">{v.name}</span>
              {v.phone && <span className="text-muted-foreground ml-2">{v.phone}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PollenDotsAddTour;
